import { ISOLanguageCode } from '@vincerosoft/assessfluence-types';
import dayjs, { ManipulateType } from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import isToday from 'dayjs/plugin/isToday';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';
import weekOfYear from 'dayjs/plugin/weekOfYear';

/** List of localized formats
 Format	  English Locale	            Sample Output
 LT	      h:mm A	                    8:02 PM
 LTS	    h:mm:ss A	                  8:02:18 PM
 L	      MM/DD/YYYY    	            08/16/2018
 LL	      MMMM D, YYYY                August 16, 2018
 LLL	    MMMM D, YYYY h:mm A	        August 16, 2018 8:02 PM
 LLLL	    dddd, MMMM D, YYYY h:mm A	  Thursday, August 16, 2018 8:02 PM
 l	      M/D/YYYY	                  8/16/2018
 ll	      MMM D, YYYY	                Aug 16, 2018
 lll	    MMM D, YYYY h:mm A	        Aug 16, 2018 8:02 PM
 llll	    ddd, MMM D, YYYY h:mm A	    Thu, Aug 16, 2018 8:02 PM
 **/

type LocalizedFormat = 'LT' | 'LTS' | 'L' | 'LL' | 'LLL' | 'LLLL' | 'l' | 'll' | 'lll' | 'llll';

dayjs.extend(weekOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(isoWeek);
dayjs.extend(calendar);
dayjs.extend(objectSupport);
dayjs.extend(isToday);

function dateFactory() {
  function toDayjs(date?: dayjs.ConfigType) {
    return dayjs(date);
  }

  function unix(timestamp: number) {
    return dayjs.unix(timestamp);
  }

  function isBefore(
    future_date: dayjs.ConfigType,
    unit?: dayjs.OpUnitType,
    past_date?: dayjs.ConfigType
  ) {
    return dayjs(past_date).isBefore(future_date, unit);
  }

  function isAfter(
    past_date: dayjs.ConfigType,
    unit?: dayjs.OpUnitType,
    future_date?: dayjs.ConfigType
  ) {
    return dayjs(future_date).isAfter(past_date, unit);
  }

  function isSame(
    date: dayjs.ConfigType,
    unit?: dayjs.OpUnitType,
    present_date?: dayjs.ConfigType
  ) {
    return dayjs(present_date).isSame(date, unit);
  }

  function setLocale(lang: ISOLanguageCode) {
    dayjs.locale(lang);
  }

  function week(date: dayjs.ConfigType = new Date()) {
    return dayjs(date).week();
  }

  function subtract(value: number, unit: ManipulateType, date?: dayjs.ConfigType) {
    return dayjs(date).subtract(value, unit);
  }

  function add(value: number, unit: ManipulateType, date?: dayjs.ConfigType) {
    return dayjs(date).add(value, unit);
  }

  function format(date: dayjs.ConfigType, template: LocalizedFormat | string = 'lll') {
    return dayjs(date).format(template);
  }

  function toIsoString(date: dayjs.ConfigType = new Date()) {
    return dayjs(date).toISOString();
  }

  function getDefaultDateRange(subtractMonths: number = 6, unit: ManipulateType = 'months') {
    // default start
    const defaultStart = dateUtils.subtract(subtractMonths, unit);
    const defaultEnd = dateUtils.toDayjs();

    const start = dateUtils.toDayjs(defaultStart);
    const end = dateUtils.toDayjs(defaultEnd);
    if (start.isAfter(end)) {
      throw new Error('Start date must be before end date');
    }

    // must be formatted to 'YYYY-MM-DD' for mantine DatePickerInput to work correctly with date ranges
    return { start: format(start, 'YYYY-MM-DD'), end: format(end, 'YYYY-MM-DD') };
  }

  return {
    toDayjs,
    unix,
    isBefore,
    isAfter,
    isSame,
    setLocale,
    week,
    subtract,
    add,
    format,
    toIsoString,
    getDefaultDateRange
  };
}

export const dateUtils = dateFactory();
